/**
 * VariationDetail (C-1c) — a single variation: FSM actions, linked product +
 * change control, history.
 * Route: /regulatory/variations/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './Regulatory.module.css';
import {
  useVariationDetailQuery, useVariationTransition,
  VARIATION_FSM, VARIATION_STATUS_LABEL, VARIATION_TYPE_LABEL,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

export function VariationDetail({ variationId }: { variationId: string }) {
  const q = useVariationDetailQuery(variationId);
  const transition = useVariationTransition(variationId);
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading variation…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Variation not found. <Link to="/regulatory/variations">Back to variations</Link></div></main>;
  const v = q.data;
  const next = VARIATION_FSM[v.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try { await transition.mutateAsync({ toStatus: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not advance the variation.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/regulatory/variations" className={styles.crumb}>Variation Management</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{v.variationCode}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{v.variationCode}</span> · {v.productName}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{VARIATION_STATUS_LABEL[v.status] ?? v.status}</span>
            <span className={styles.sub}>{VARIATION_TYPE_LABEL[v.variationType] ?? v.variationType} · {v.authority} · {v.market}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {next.length === 0 ? <span className={styles.terminal}>Terminal status</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'WITHDRAWN' || to === 'REJECTED' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>→ {VARIATION_STATUS_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Variation detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Type</dt><dd>{VARIATION_TYPE_LABEL[v.variationType] ?? v.variationType}</dd></div>
          <div><dt>Authority</dt><dd>{v.authority}</dd></div>
          <div><dt>Market</dt><dd>{v.market}</dd></div>
          <div><dt>Submitted</dt><dd>{fmtDate(v.submissionDate)}</dd></div>
          <div><dt>Approved</dt><dd>{fmtDate(v.approvalDate)}</dd></div>
          <div><dt>Linked product</dt><dd>{v.productId
            ? <Link to="/regulatory/products/$id" params={{ id: v.productId }} className={styles.crumb}>View in register</Link> : '—'}</dd></div>
          <div><dt>Change control</dt><dd>{v.changeRequestId
            ? <Link to="/changes" className={styles.crumb}>{v.changeRequestTitle ?? 'View change control'}</Link> : '—'}</dd></div>
        </dl>
        <div className={styles.descBlock}><strong>Description</strong><p>{v.description}</p></div>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(v.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.from_status ? `${VARIATION_STATUS_LABEL[h.from_status] ?? h.from_status} → ${VARIATION_STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}` : (h.event_kind.replaceAll('_', ' ').toLowerCase())}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
